import { useCallback, useEffect, useRef, useState } from "react";

const POLL_MS = 60_000;

// Fingerprint the running build from the hashed JS/CSS referenced in index.html
// (production) with a /version.json fallback so it also works on the dev server.
// Returns { fingerprint, assets } — assets are same-origin URLs we can prefetch.
async function fetchBuildInfo() {
  let assets = [];
  let fingerprint = "";
  try {
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" });
    if (res.ok) {
      const html = await res.text();
      const urls = new Set();
      const re = /(?:src|href)="(\/[^"]*\.(?:js|css))"/g;
      let m;
      while ((m = re.exec(html))) urls.add(m[1]);
      assets = [...urls];
      fingerprint = assets.slice().sort().join("|");
    }
  } catch {
    /* offline / blocked — ignore */
  }
  // Always fold in version.json so an explicit build bump is detected even when
  // asset URLs are unhashed (dev) or unchanged.
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" });
    if (res.ok) {
      const j = await res.json();
      if (j && j.version) fingerprint = `${fingerprint}#v:${j.version}`;
    }
  } catch {
    /* ignore */
  }
  return { fingerprint, assets };
}

// Update lifecycle:
//   idle -> (new build detected) -> downloading (0..100) -> ready -> (refresh)
export function useAppUpdate() {
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const baseline = useRef(null);
  const busy = useRef(false);
  const statusRef = useRef("idle");
  statusRef.current = status;

  const downloadAssets = useCallback(async (assets) => {
    setStatus("downloading");
    setProgress(0);
    const list = assets && assets.length ? assets : [];
    if (!list.length) {
      // Nothing hashed to prefetch (e.g. dev) — still surface the ready state.
      setProgress(100);
      setStatus("ready");
      return;
    }
    let done = 0;
    for (const url of list) {
      try {
        await fetch(url, { cache: "reload" });
      } catch {
        /* keep going — a failed prefetch just means a slower reload */
      }
      done += 1;
      setProgress(Math.round((done / list.length) * 100));
    }
    setProgress(100);
    setStatus("ready");
  }, []);

  const check = useCallback(async () => {
    if (busy.current || statusRef.current !== "idle") return;
    const info = await fetchBuildInfo();
    if (!info.fingerprint) return;
    if (baseline.current == null) {
      baseline.current = info.fingerprint; // establish the loaded build
      return;
    }
    if (info.fingerprint !== baseline.current) {
      busy.current = true;
      baseline.current = info.fingerprint; // adopt so we don't re-trigger
      try {
        const reg = await navigator.serviceWorker?.getRegistration?.();
        reg?.update?.();
      } catch {
        /* ignore */
      }
      await downloadAssets(info.assets);
    }
  }, [downloadAssets]);

  useEffect(() => {
    check(); // establish baseline right away
    const id = setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.__mgCheckUpdate = check; // manual trigger (used by tests)
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      if (window.__mgCheckUpdate === check) delete window.__mgCheckUpdate;
    };
  }, [check]);

  const applyUpdate = useCallback(async () => {
    setStatus("idle"); // unmount the banner immediately on refresh
    try {
      const reg = await navigator.serviceWorker?.getRegistration?.();
      if (reg?.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
    } catch {
      /* ignore — fall through to a plain reload */
    }
    setTimeout(() => window.location.reload(), 120);
  }, []);

  return { status, progress, applyUpdate };
}
